import type {
  ApplicationJourneyHeaderDto,
  ApplicationOnboardingSignalsDto,
  ApplicationTimelineEventDto,
} from '../api/admin';
import type { IdentityAttemptPresentation } from './identityAttemptPresentation';
import { resolveReturningCustomerOutcome } from './identityEvidenceQuestions';
import type { IdentityMatchInsights } from './identityMatchInsights';
import type { IdentitySummaryTone } from './identitySessionSummary';

export type IdentityPostMatchFact = {
  /** Short operator verb / status (e.g. Yes, Kept same, Same account confirmed). */
  value: string;
  tone: IdentitySummaryTone;
  /** One plain sentence under the station. */
  detail: string | null;
};

/** Post-signin prompt: keep account contacts vs update to new ones. */
export type IdentityContactChoiceDecision =
  | 'kept_existing'
  | 'updated_to_new'
  | 'pending'
  | 'unclear';

export type IdentityContactChannelRow = {
  channel: 'email' | 'phone';
  /** Contacts on the matched account (what “keep existing” would use). */
  onAccount: string | null;
  /** Contacts from this application attempt (what “update” would use / started with). */
  fromThisApplication: string | null;
  /** True when national digits / email match ignoring formatting. */
  sameAsAccount: boolean;
};

export type IdentityContactChoiceInsight = {
  decision: IdentityContactChoiceDecision;
  fact: IdentityPostMatchFact;
  channels: IdentityContactChannelRow[];
  /** Optional note e.g. phone formatting-only. */
  note: string | null;
};

export type IdentityAccountContinuityInsight = {
  visible: boolean;
  signedIn: IdentityPostMatchFact;
  contacts: IdentityPostMatchFact;
  /** @deprecated use contactChoice.channels — kept for older callers/tests. */
  bridges: Array<{
    channel: 'email' | 'phone';
    onFile: string | null;
    attempted: string | null;
    changed: boolean;
  }>;
  contactChoice: IdentityContactChoiceInsight;
};

export type IdentityReturningFlowInsight = {
  visible: boolean;
  outcome: IdentityPostMatchFact;
  customerName: string | null;
  ccid: string | null;
  attemptStatus: string | null;
  selfieSummary: string | null;
  selfieDistance: string | null;
};

export type IdentityPostMatchInsights = {
  accountContinuity: IdentityAccountContinuityInsight;
  returningFlow: IdentityReturningFlowInsight;
};

/** @deprecated Prefer IdentityContactChannelRow via contactChoice. */
export type IdentityContactBridge = IdentityAccountContinuityInsight['bridges'][number];

function str(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

function emailsEquivalent(a: string | null, b: string | null): boolean | null {
  if (!a || !b) return null;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Treat +91… / (891) … / 891… as the same when national digits align. */
function phonesEquivalent(a: string | null, b: string | null): boolean | null {
  if (!a || !b) return null;
  const da = a.replace(/\D/g, '');
  const db = b.replace(/\D/g, '');
  if (!da || !db) return null;
  const ta = da.length >= 10 ? da.slice(-10) : da;
  const tb = db.length >= 10 ? db.slice(-10) : db;
  return ta === tb;
}

function hasMatchedProfile(
  attempts: IdentityAttemptPresentation[],
  matchInsights: IdentityMatchInsights,
): boolean {
  if (matchInsights.rows.some((r) => r.role === 'primary_hit')) return true;
  if (attempts.some((a) => a.role === 'existing_found')) return true;
  return attempts.some(
    (a) =>
      a.row.latestDedup?.isDuplicate === true ||
      String(a.row.session_status ?? '').toUpperCase() === 'REJECTED_DUPLICATE',
  );
}

function otpVerified(
  onboarding: ApplicationOnboardingSignalsDto | null,
  application: ApplicationJourneyHeaderDto | null,
): boolean {
  if (application?.phoneVerifiedAt) return true;
  if (onboarding?.phoneVerifiedAt) return true;
  const channels = onboarding?.otpAttempts?.channels ?? [];
  return channels.some((ch) => (ch.verifySuccess ?? 0) > 0);
}

function otpFailed(onboarding: ApplicationOnboardingSignalsDto | null): boolean {
  const channels = onboarding?.otpAttempts?.channels ?? [];
  return channels.some((ch) => (ch.verifyFailed ?? 0) > 0 || (ch.lockedSessions ?? 0) > 0);
}

function customerDisplayName(parts: {
  first_name?: string | null;
  last_name?: string | null;
}): string | null {
  const name = [parts.first_name, parts.last_name].filter(Boolean).join(' ').trim();
  return name || null;
}

function readTimelineContactChoice(
  timeline: ApplicationTimelineEventDto[] | null | undefined,
): {
  decision: 'kept_existing' | 'updated_to_new';
  previousEmail: string | null;
  newEmail: string | null;
  previousPhone: string | null;
  newPhone: string | null;
} | null {
  if (!timeline?.length) return null;
  for (let i = timeline.length - 1; i >= 0; i -= 1) {
    const ev = timeline[i]!;
    const title = String(ev.title ?? '').toUpperCase();
    if (title !== 'RESOLVE_DUPLICATE_CONTACT') continue;
    const decision = str(ev.detail?.decision)?.toLowerCase() ?? '';
    if (decision !== 'keep-existing-contact' && decision !== 'update-profile-contact') {
      continue;
    }
    return {
      decision: decision === 'keep-existing-contact' ? 'kept_existing' : 'updated_to_new',
      previousEmail: str(ev.detail?.previousEmail) || str(ev.detail?.canonicalEmail),
      newEmail: str(ev.detail?.newEmail),
      previousPhone: str(ev.detail?.previousPhone),
      newPhone: str(ev.detail?.newPhone),
    };
  }
  return null;
}

function buildSignedInFact(
  attempts: IdentityAttemptPresentation[],
  onboarding: ApplicationOnboardingSignalsDto | null,
  application: ApplicationJourneyHeaderDto | null,
): IdentityPostMatchFact {
  const after = attempts.find((a) => a.role === 'after_signin');
  const existing = attempts.find((a) => a.role === 'existing_found');
  const existingStatus = String(existing?.row.session_status ?? '').toUpperCase();
  const verified = otpVerified(onboarding, application);
  const failed = otpFailed(onboarding);

  if (after) {
    const status = String(after.row.session_status ?? '').toUpperCase();
    const bio = after.row.biometricMatchComparison;
    if (bio?.kind === 'RETURNING_SELFIE_1TO1' && bio.passed === false) {
      return {
        value: 'Signed in — selfie failed',
        tone: 'error',
        detail: 'They reached the matched account, but the returning selfie check failed.',
      };
    }
    if (
      status === 'SUCCESS' ||
      status === 'COMPLETED' ||
      status === 'IDV_COMPLETED' ||
      bio?.passed === true
    ) {
      return {
        value: 'Authenticated',
        tone: 'success',
        detail: 'They signed in to the matched account and continued the journey.',
      };
    }
    return {
      value: 'In progress',
      tone: 'info',
      detail: 'Sign-in started for the matched account; checks are still running.',
    };
  }

  if (existingStatus === 'REJECTED_DUPLICATE' || existingStatus === 'REJECTED' || existingStatus === 'FAILED') {
    return {
      value: 'Blocked',
      tone: 'error',
      detail: 'Duplicate match blocked the application before sign-in.',
    };
  }

  if (
    existingStatus === 'PENDING_EXISTING_ACCOUNT_LINK' ||
    existingStatus === 'LINKED_PENDING_AUTH'
  ) {
    if (failed && !verified) {
      return {
        value: 'Sign-in failed',
        tone: 'error',
        detail: 'OTP or sign-in attempts failed while linking to the matched account.',
      };
    }
    return {
      value: 'Awaiting sign-in',
      tone: 'warning',
      detail: 'Match found — they still need to authenticate to the account on file.',
    };
  }

  if (verified) {
    return {
      value: 'Verified contact',
      tone: 'success',
      detail: 'Contact verification succeeded after the match; returning identity attempt is not on file yet.',
    };
  }

  return {
    value: 'Unclear',
    tone: 'info',
    detail: 'A match exists, but there is no clear after-sign-in attempt yet.',
  };
}

function buildContactChoiceInsight(
  onboarding: ApplicationOnboardingSignalsDto | null,
  application: ApplicationJourneyHeaderDto | null,
  timeline: ApplicationTimelineEventDto[] | null | undefined,
  signedIn: IdentityPostMatchFact,
): IdentityContactChoiceInsight {
  const timelineChoice = readTimelineContactChoice(timeline);

  // After "Yes, update", app.email/phone and emailOnFile are already the NEW values —
  // prefer timeline previous→new (signed-in account before resolve).
  const onAccountEmail =
    (timelineChoice?.decision === 'updated_to_new'
      ? timelineChoice.previousEmail
      : null) ||
    str(onboarding?.emailOnFile) ||
    str(application?.email);
  const onAccountPhone =
    (timelineChoice?.decision === 'updated_to_new'
      ? timelineChoice.previousPhone
      : null) ||
    str(onboarding?.phoneOnFile) ||
    str(application?.phone);

  const startedEmail =
    (timelineChoice?.decision === 'updated_to_new'
      ? timelineChoice.newEmail
      : null) ||
    str(onboarding?.attemptedEmail) ||
    str(application?.attemptedEmail) ||
    (timelineChoice?.decision === 'updated_to_new' ? str(application?.email) : null);

  const startedPhone =
    (timelineChoice?.decision === 'updated_to_new'
      ? timelineChoice.newPhone
      : null) ||
    str(onboarding?.attemptedPhone) ||
    str(application?.attemptedPhone) ||
    (timelineChoice?.decision === 'updated_to_new' ? str(application?.phone) : null);

  const emailSame = emailsEquivalent(startedEmail, onAccountEmail) === true;
  const phoneSame = phonesEquivalent(startedPhone, onAccountPhone) === true;
  const phoneFormattingOnly =
    phoneSame &&
    Boolean(startedPhone && onAccountPhone) &&
    startedPhone!.replace(/\D/g, '') !== onAccountPhone!.replace(/\D/g, '');

  const channels: IdentityContactChannelRow[] = [];
  if (onAccountEmail || startedEmail) {
    channels.push({
      channel: 'email',
      onAccount: onAccountEmail,
      fromThisApplication: startedEmail,
      sameAsAccount: emailSame || (!startedEmail && Boolean(onAccountEmail)),
    });
  }
  if (onAccountPhone || startedPhone) {
    channels.push({
      channel: 'phone',
      onAccount: onAccountPhone,
      fromThisApplication: startedPhone,
      sameAsAccount: phoneSame || (!startedPhone && Boolean(onAccountPhone)),
    });
  }

  const startedDiffers =
    (Boolean(startedEmail) && emailsEquivalent(startedEmail, onAccountEmail) === false) ||
    (Boolean(startedPhone) && phonesEquivalent(startedPhone, onAccountPhone) === false);

  let decision: IdentityContactChoiceDecision;
  if (timelineChoice) {
    decision = timelineChoice.decision;
  } else if (
    signedIn.value === 'Awaiting sign-in' ||
    signedIn.value === 'Sign-in failed' ||
    signedIn.value === 'Unclear'
  ) {
    decision = 'pending';
  } else if (!startedEmail && !startedPhone) {
    decision = 'kept_existing';
  } else if (!startedDiffers) {
    decision = 'kept_existing';
  } else {
    const appEmail = str(application?.email);
    const appPhone = str(application?.phone);
    const appMatchesAttempt =
      (startedEmail && emailsEquivalent(appEmail, startedEmail) === true) ||
      (startedPhone && phonesEquivalent(appPhone, startedPhone) === true);
    const appMatchesAccount =
      (onAccountEmail && emailsEquivalent(appEmail, onAccountEmail) === true) ||
      (onAccountPhone && phonesEquivalent(appPhone, onAccountPhone) === true);

    if (appMatchesAttempt && !appMatchesAccount) decision = 'updated_to_new';
    else if (appMatchesAccount) decision = 'kept_existing';
    else decision = 'unclear';
  }

  const anyChannelChanged = channels.some((c) => !c.sameAsAccount && Boolean(c.fromThisApplication));
  const note =
    decision === 'updated_to_new' && !anyChannelChanged && phoneFormattingOnly
      ? 'Chose “Yes, update” — phone digits already matched the account (formatting only).'
      : decision === 'updated_to_new' && !anyChannelChanged
        ? 'Chose “Yes, update” — email/phone already matched the signed-in account.'
        : phoneFormattingOnly && decision !== 'updated_to_new'
          ? 'Phone digits match — formatting only (+91 vs local).'
          : phoneFormattingOnly
            ? 'Phone digits match — formatting only (+91 vs local).'
            : null;

  if (decision === 'kept_existing') {
    return {
      decision,
      fact: {
        value: 'Kept existing contacts',
        tone: 'success',
        detail: startedDiffers
          ? 'After sign-in they chose “No, keep existing” — the matched account email/phone stay in use. Any different details from this application were not applied.'
          : 'After sign-in they continued with the email and phone already on the matched account.',
      },
      channels,
      note,
    };
  }

  if (decision === 'updated_to_new') {
    return {
      decision,
      fact: {
        value: 'Updated to new contacts',
        tone: 'warning',
        detail: anyChannelChanged
          ? 'After sign-in they chose “Yes, update” — the matched account moved from the contacts they verified on to the email/phone from this application.'
          : 'After sign-in they chose “Yes, update”, but the contacts from this application already matched the signed-in account.',
      },
      channels,
      note,
    };
  }

  if (decision === 'pending') {
    return {
      decision,
      fact: {
        value: 'Awaiting contact choice',
        tone: 'warning',
        detail:
          'After OTP they are asked whether to keep the matched account contacts or update to the ones from this application.',
      },
      channels,
      note,
    };
  }

  return {
    decision: 'unclear',
    fact: {
      value: 'Contact choice unclear',
      tone: 'info',
      detail:
        'Could not tell whether they kept the matched account contacts or updated to new ones.',
    },
    channels,
    note,
  };
}

function buildReturningFlow(
  attempts: IdentityAttemptPresentation[],
  matched: boolean,
): IdentityReturningFlowInsight {
  const empty: IdentityReturningFlowInsight = {
    visible: false,
    outcome: { value: '—', tone: 'info', detail: null },
    customerName: null,
    ccid: null,
    attemptStatus: null,
    selfieSummary: null,
    selfieDistance: null,
  };
  if (!matched) return empty;

  const after = attempts.find((a) => a.role === 'after_signin');
  const hitName =
    customerDisplayName(attempts.find((a) => a.role === 'existing_found')?.row.latestDedup?.matched_hit_customer ?? {}) ||
    null;

  if (!after) {
    return {
      visible: true,
      outcome: {
        value: 'Not started',
        tone: 'warning',
        detail: 'Waiting for sign-in before returning-customer identity checks run.',
      },
      customerName: hitName,
      ccid: null,
      attemptStatus: null,
      selfieSummary: null,
      selfieDistance: null,
    };
  }

  const resolved = resolveReturningCustomerOutcome(after.row);
  const bio = after.row.biometricMatchComparison;
  const name =
    customerDisplayName(after.row.customer ?? {}) ||
    hitName;

  let selfieSummary: string | null = null;
  let selfieDistance: string | null = null;
  const route = String(after.row.verification_route ?? '').toUpperCase();
  const status = String(after.row.session_status ?? '').toUpperCase();
  const completedOk =
    status === 'SUCCESS' || status === 'COMPLETED' || status === 'IDV_COMPLETED';

  if (bio?.kind === 'RETURNING_SELFIE_1TO1') {
    selfieSummary =
      bio.passed === true || (bio.passed !== false && completedOk)
        ? 'Login selfie matched on-file biometric'
        : bio.passed === false
          ? 'Login selfie did not match on-file biometric'
          : 'Login selfie compared to on-file biometric';
    if (bio.distance != null) selfieDistance = Number(bio.distance).toFixed(4);
  } else if (bio?.kind === 'DEDUP_BIOMETRIC_HIT') {
    selfieSummary =
      bio.passed === true
        ? 'Biometric matched the account on file'
        : bio.passed === false
          ? 'Biometric did not match the account on file'
          : 'Biometric comparison ran against the account on file';
    if (bio.distance != null) selfieDistance = Number(bio.distance).toFixed(4);
  } else if (route === 'RETURNING_SELFIE_1TO1' && completedOk) {
    selfieSummary = 'Returning biometric selfie check passed';
  } else if (completedOk && !after.row.mitekCalled) {
    selfieSummary =
      'Selfie-only returning — new ID / Mitek skipped (on-file expiry more than 30 days out)';
  } else if (after.row.mitekCalled) {
    selfieSummary = 'Document authenticity (Mitek) also ran on this returning attempt';
  }

  return {
    visible: true,
    outcome: {
      value: resolved.answer,
      tone: resolved.tone,
      detail: resolved.body || null,
    },
    customerName: name,
    ccid: after.row.ccid,
    attemptStatus: String(after.row.session_status ?? '').replace(/_/g, ' ') || null,
    selfieSummary,
    selfieDistance,
  };
}

/**
 * Post–Match neighborhood story: sign-in → contact choice → returning confirmation.
 * Hidden when no profile was matched.
 */
export function buildIdentityPostMatchInsights(input: {
  attempts: IdentityAttemptPresentation[];
  matchInsights: IdentityMatchInsights;
  application?: ApplicationJourneyHeaderDto | null;
  onboarding?: ApplicationOnboardingSignalsDto | null;
  timeline?: ApplicationTimelineEventDto[] | null;
}): IdentityPostMatchInsights {
  const attempts = input.attempts;
  const matched = hasMatchedProfile(attempts, input.matchInsights);
  const application = input.application ?? null;
  const onboarding = input.onboarding ?? null;

  const emptyChoice = buildContactChoiceInsight(null, null, null, {
    value: '—',
    tone: 'info',
    detail: null,
  });

  if (!matched) {
    return {
      accountContinuity: {
        visible: false,
        signedIn: { value: '—', tone: 'info', detail: null },
        contacts: { value: '—', tone: 'info', detail: null },
        bridges: [],
        contactChoice: emptyChoice,
      },
      returningFlow: buildReturningFlow(attempts, false),
    };
  }

  const signedIn = buildSignedInFact(attempts, onboarding, application);
  const contactChoice = buildContactChoiceInsight(
    onboarding,
    application,
    input.timeline,
    signedIn,
  );

  return {
    accountContinuity: {
      visible: true,
      signedIn,
      contacts: contactChoice.fact,
      bridges: contactChoice.channels.map((c) => ({
        channel: c.channel,
        onFile: c.onAccount,
        attempted: c.fromThisApplication,
        changed: !c.sameAsAccount && Boolean(c.fromThisApplication),
      })),
      contactChoice,
    },
    returningFlow: buildReturningFlow(attempts, true),
  };
}
